import React, { useState } from 'react';
import { useHistory } from 'react-router';
import sortBy from 'lodash/sortBy';
import Button from '@medflex/components/stories/Button';
import TextSnippetsModal from '@medflex/components/stories/TextSnippetsModal';
import { FEATURE_KEYS } from '@medflex/utils/subscriptionEnums';
import { FileInput } from '@medflex/components/stories/FileUpload';
import { trackEvent } from '@medflex/utils/Tracking';
import ContextMenu from '@medflex/components/stories/ContextMenu';
import Badge, { KINDS as BADGE_KINDS } from '@medflex/components/stories/Badge';
import {
  IconAddSolid,
  IconAttachment,
  IconListAdd,
  IconUserGroup,
} from '@medflex/components/stories/Icons';
import TextSnippetsEditModal from '../TextSnippetsEditModal';
import useFeatures from '../../hooks/useFeatures';
import useModal from '../../hooks/useModal';
import useSnippets from '../../hooks/useSnippets';
import useSelectedContact from '../../hooks/useSelectedContact';
import { PhysicianContextMenuPropTypes } from './propTypes';

const PhysicianContextMenu = ({
  registerFileInput,
  actionItems,
  t,
  isWideScreen,
  isDisabled,
  insertSnippet,
  isPamSession,
}) => {
  const [textSnippets, multicast] = useFeatures([
    FEATURE_KEYS.TEXT_SNIPPETS,
    FEATURE_KEYS.MULTICAST_MESSAGE,
  ]);
  const [textSnippetsModalShown, setTextSnippetsModalShown] = useState(false);
  const [textSnippetsEditModalShown, setTextSnippetsEditModalShown] = useState(false);
  const { open } = useModal();
  const { push } = useHistory();
  const { snippets } = useSnippets();
  const { selectedContact } = useSelectedContact();

  const toggleSnippetsModal = () => {
    setTextSnippetsModalShown(!textSnippetsModalShown);
  };

  const handleCreateSnippet = () => {
    toggleSnippetsModal();
    setTextSnippetsEditModalShown(true);
  };

  const handleChangeSnippet = () => {
    toggleSnippetsModal();
    push('/settings/snippets');
  };

  const handleInsertSnippet = ({ id: snippetId }) => {
    const { text, files } = snippets.find(sn => sn.id === snippetId);
    insertSnippet({ text, files });

    toggleSnippetsModal();
  };

  const handleOpenMulticast = event => {
    trackEvent(event);
    open({ modal: 'multicast', state: { selectedContact } });
  };

  const onTextSnippetsEditModalClose = snippet => {
    setTextSnippetsEditModalShown(false);
    if (!snippet?.text) return;
    insertSnippet(snippet.text);
  };

  const goBackToSnippetsList = () => {
    setTextSnippetsEditModalShown(false);
    toggleSnippetsModal();
  };

  return (
    <>
      <FileInput {...registerFileInput()}>
        {({ select }) => (
          <ContextMenu>
            <ContextMenu.Menu closeButtonLabel={t('global:actions.close_button')}>
              {actionItems.map((item, index) => (
                <ContextMenu.Group key={index}>
                  <ContextMenu.Item onClick={item.onClick}>
                    <span className="flex items-center text-lg text-blue-500">{item.icon}</span>
                    <span className="ml-3">{item.text}</span>
                  </ContextMenu.Item>
                </ContextMenu.Group>
              ))}
              {!isPamSession && (
                <ContextMenu.Group>
                  <ContextMenu.Item
                    onClick={
                      multicast && !multicast.active
                        ? () => open({ modal: 'featureUpgradeMulticast' })
                        : handleOpenMulticast
                    }
                  >
                    <span className="flex items-center text-lg text-blue-500">
                      <IconUserGroup size="text" />
                    </span>
                    <span className="ml-3">{t('global:actions.multicast.menu')}</span>
                    {textSnippets && !textSnippets.active && (
                      <Badge className="ml-2" kind={BADGE_KINDS.SIGNAL}>
                        {t('global:actions.upgrade')}
                      </Badge>
                    )}
                  </ContextMenu.Item>
                </ContextMenu.Group>
              )}

              <ContextMenu.Group>
                <ContextMenu.Item
                  onClick={
                    textSnippets && !textSnippets.active
                      ? () => open({ modal: 'featureUpgradeSnippets' })
                      : toggleSnippetsModal
                  }
                >
                  <span className="flex items-center text-lg text-blue-500">
                    <IconListAdd size="text" />
                  </span>
                  <span className="ml-3">{t('global:actions.text_snippets.menu')}</span>
                  {textSnippets && !textSnippets.active && (
                    <Badge className="ml-2" kind={BADGE_KINDS.SIGNAL}>
                      {t('global:actions.upgrade')}
                    </Badge>
                  )}
                </ContextMenu.Item>
              </ContextMenu.Group>
              <ContextMenu.Group>
                <ContextMenu.Item onClick={select}>
                  <span className="flex rotate-90 transform items-center text-lg text-blue-500">
                    <IconAttachment size="text" />
                  </span>
                  <span className="ml-3">{t('global:actions.file_upload.menu')}</span>
                </ContextMenu.Item>
              </ContextMenu.Group>
            </ContextMenu.Menu>
            <ContextMenu.Trigger>
              {({ triggerRef, isVisible }) => (
                <Button
                  kind={isWideScreen ? 'primary' : 'primary:ghost'}
                  className="lg:mx-2"
                  shape={isWideScreen ? 'square' : 'circle'}
                  innerRef={triggerRef}
                  isActive={isVisible}
                  isDisabled={isDisabled}
                >
                  <IconAddSolid size="6" />
                </Button>
              )}
            </ContextMenu.Trigger>
          </ContextMenu>
        )}
      </FileInput>
      {textSnippetsModalShown && (
        <TextSnippetsModal
          snippets={sortBy(snippets, [s => s.title.toLowerCase()])}
          onEdit={handleChangeSnippet}
          onCreate={handleCreateSnippet}
          onSelected={handleInsertSnippet}
          onClose={toggleSnippetsModal}
        />
      )}
      {textSnippetsEditModalShown && (
        <TextSnippetsEditModal
          onGoBackClick={goBackToSnippetsList}
          onClose={onTextSnippetsEditModalClose}
        />
      )}
    </>
  );
};

PhysicianContextMenu.propTypes = PhysicianContextMenuPropTypes;

export default PhysicianContextMenu;
